import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CalendarClock, ShieldCheck, Ban, LayoutTemplate, Link2, Lock, Loader2 } from "lucide-react";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Landing() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) { toast.error("Enter your email and password"); return; }
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const features = [
    { icon: CalendarClock, title: "Best-hours availability", desc: "Share only the deep-work-friendly slots you choose." },
    { icon: Ban, title: "Daily meeting caps", desc: "Hit your limit and the calendar auto-locks to Unbookable." },
    { icon: LayoutTemplate, title: "Scenario templates", desc: "Client calls, board meetings, demos — each with its own rules." },
    { icon: Link2, title: "One public link", desc: "Send a link, kill the back-and-forth, get booked." },
  ];

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <header className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-2" data-testid="cliq2book-logo">
          <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center">
            <CalendarClock className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-extrabold tracking-tight">Cliq2Book</span>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 pt-12 pb-20 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-blue-700 bg-blue-50 border border-blue-100 rounded-full px-3 py-1 mb-6">
            <ShieldCheck className="w-3.5 h-3.5" /> Deep-work protected scheduling
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
            Book meetings.<br />Protect your focus.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed">
            Cliq2Book shares only your optimal hours, enforces daily meeting caps automatically, and makes your calendar unbookable the moment you hit your threshold.
          </p>
          <div className="mt-8 grid sm:grid-cols-2 gap-4 max-w-xl">
            {features.map((f) => (
              <div key={f.title} className="flex items-start gap-3">
                <div className="w-9 h-9 shrink-0 rounded-lg bg-blue-600 flex items-center justify-center">
                  <f.icon className="w-4.5 h-4.5 text-white" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold">{f.title}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="w-full max-w-md mx-auto lg:ml-auto">
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8">
            <div className="flex items-center gap-2 mb-1">
              <Lock className="w-4 h-4 text-blue-600" />
              <h2 className="text-lg font-bold">Admin sign in</h2>
            </div>
            <p className="text-sm text-slate-500 mb-6">Sign in to set up your availability and templates.</p>
            <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Email</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input"
                  placeholder="admin@cliq2book.com" autoComplete="username"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Password</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input"
                  placeholder="••••••••" autoComplete="current-password"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
              </div>
              <button type="submit" disabled={submitting} className="brand-btn w-full py-2.5" data-testid="login-submit-btn">
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</> : "Sign in"}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
